/**
 * Phase 17 — the pool ranked by its own results on this campaign group's
 * tracker (grouped by gateway), best first: each gateway's score, the
 * formula's values, its share when the weights follow the formula, and what
 * the rules said. One row per LANE (Phase 40): a gateway with two lanes
 * shows both under its rank, its formula share split between them.
 * The live panel of the Gateway strategy pop-up.
 * For Media Devoted | Created By Ciro Autiero
 */
import { trackerFromEnum, trackerLabel } from '@trafficpulse/shared';
import { FC } from 'react';
import { gatewayFormulaWeights, hourRangeLabel } from '@trafficpulse/shared';

import type { DraftGateway } from '../../../../drop-generator.types';
import { rankingColumns } from '../../creative/ranking/results/ranking-columns';
import { barScale } from '../../creative/ranking/results/results-grid';
import { notEnoughDataValue } from '../../rules/rule-model';
import { TagColorsProvider } from '../../rules/TagChips';
import { TagReadNotes } from '../../rules/TagReadNotes';
import { poolRouteIds } from './formula-pool';
import { RouteRow } from './RouteRow';
import type { GatewayRankingQuery } from './use-gateway-ranking';

export const RankedRoutes: FC<{
  gateway: DraftGateway;
  data: NonNullable<GatewayRankingQuery['data']>;
  routeName: (routeId: number) => string;
  /** The open lane's id. */
  open: string | null;
  onOpen: (laneId: string) => void;
  loading: boolean;
}> = ({ gateway, data, routeName, open, onOpen, loading }) => {
  const { columns } = rankingColumns(data.formula);
  const bar = barScale(data.routes.map((r) => r.score));
  const pool = gateway.pool;
  const shares = gateway.weightsMode === 'FORMULA' ? gatewayFormulaWeights(poolRouteIds(gateway), data.routes) : null;
  const floor = { minVolume: notEnoughDataValue(gateway.ranking.rules), rangeLabel: hourRangeLabel(data.primaryRangeKey), primaryRangeKey: data.primaryRangeKey };
  const rows = data.routes.flatMap((r) => {
    const lanes = pool.flatMap((lane, poolIndex) => (lane.routeId === r.routeId ? [{ lane, poolIndex }] : []));
    return lanes.map(({ lane, poolIndex }) => ({ r, lane, poolIndex, share: shares ? (shares.get(r.routeId) ?? 0) / lanes.length : null }));
  });
  return (
    <TagColorsProvider colors={data.tagColors}>
      <div className="flex flex-col gap-1.5">
        <TagReadNotes read={data} surface="gateways" />
        <span className="text-[11px] text-muted-foreground">
          {data.rankedRoutes} of {data.routes.length} gateways ranked · {trackerLabel(trackerFromEnum(data.provider))}
          {loading ? ' · updating…' : ''}
        </span>
        <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
          {rows.map(({ r, lane, poolIndex, share }) => (
            <RouteRow key={lane.id} r={r} lane={lane} poolIndex={poolIndex} name={routeName(r.routeId)} columns={columns} formula={data.formula} bar={bar(r.score)} share={share} open={open === lane.id} onOpen={() => onOpen(lane.id)} floor={floor} />
          ))}
        </ul>
      </div>
    </TagColorsProvider>
  );
};
